/**
 * PATH: packages/job-hub/src/jobs-list/v2JobsAdapter.ts
 * FILE_ID: JOBHUB-JOBSLIST-V2ADAPTER-001
 * PURPOSE: Pure reshape of the Django /api/v2/jobs/ payload (paginated
 *          {results,...} or bare array) into the shared JobListRow contract
 *          the lifted jobs-list frames render.
 * WHY: W5b — the shared list must run on BOTH backends. sam-api /jobs
 *      already returns JobListRow-shaped rows; the standard Django platform
 *      serves JobListV2Serializer rows (numbers not strings, no win/stage
 *      forks, served health/WIP fields). This is the seam that makes the
 *      Django payload satisfy the tenant contract — the row-level twin of
 *      src/reducers/adapters/jobsV2Adapter.js (Redux legacy shape).
 * LANDMINE: money fields become STRINGS here on purpose (tenant JobRow
 *      contract; inline editors parseFloat them). status is lower-cased —
 *      legacy Django rows carry mixed casing.
 * RELATED:
 * - packages/job-hub/src/jobs/djangoJobHubClient.ts (calls this in reshape)
 * - report/serializers_v2/job_serializers.py (source shape)
 */
import type { JobsListPage } from './gateway/JobsListGateway'
import type { JobListRow } from './types'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

type CustomFieldValue = string | number | boolean | null

const isCustomFieldValue = (value: unknown): value is CustomFieldValue =>
  value === null
  || typeof value === 'string'
  || typeof value === 'number'
  || typeof value === 'boolean'

/** Keep only primitives the custom-field editors can round-trip. */
function customFields(value: unknown): NonNullable<JobListRow['custom_fields']> {
  if (!isRecord(value)) return {}
  const projected: Record<string, CustomFieldValue> = {}
  Object.entries(value).forEach(([key, entry]) => {
    if (isCustomFieldValue(entry)) projected[key] = entry
  })
  return projected
}

/**
 * `year` is not a Job column. The detail hub derives it from the start date
 * (`report/views/job_detail_hub.py`, `"year": start.year`) and the list
 * serializer never sends it, so the list has to derive it the same way or the
 * two surfaces disagree about the same job.
 */
function yearOf(served: unknown, startDate: unknown): number | null {
  if (served != null) {
    const direct = Number(served)
    if (Number.isFinite(direct)) return direct
  }
  if (typeof startDate !== 'string') return null
  const parsed = Number(startDate.slice(0, 4))
  return Number.isFinite(parsed) && parsed > 1900 ? parsed : null
}

/** One Django V2 row → JobListRow. Pure I→O. */
export function mapV2JobToRow(v2: Record<string, unknown>): JobListRow {
  const num = (x: unknown): number => {
    const n = Number(x)
    return Number.isFinite(n) ? n : 0
  }
  const nullableNumber = (x: unknown): number | null => x == null ? null : num(x)
  const nullableString = (x: unknown): string | null => x == null ? null : String(x)
  const record = (x: unknown): Record<string, unknown> | null =>
    Boolean(x) && typeof x === 'object' && !Array.isArray(x)
      ? x as Record<string, unknown>
      : null
  const client = record(v2.client)
  const hierarchy = record(v2.hierarchy)
  const access = record(v2.access) ?? record(v2.access_metadata)
  return {
    id: num(v2.id),
    job_number: String(v2.job_number ?? v2.id ?? ''),
    client_name: nullableString(v2.client_name),
    client_id: v2.client_id != null ? num(v2.client_id) : nullableNumber(client?.id),
    client: client ? { id: num(client.id), name: String(client.name ?? v2.client_name ?? '') } : null,
    site_address: nullableString(v2.site_address || v2.site_name),
    scope: nullableString(v2.scope),
    scope_label: nullableString(v2.scope_label),
    contract_value: v2.contract_value != null ? String(v2.contract_value) : null,
    quote_estimated_value: v2.quote_estimated_value != null ? String(v2.quote_estimated_value) : null,
    total_invoiced: String(v2.invoiced_amount ?? 0),
    // `remaining` is the historical balance alias in the shared row
    // contract; prefer the explicit backend field when it is available.
    remaining: String(v2.wip_balance ?? v2.wip ?? 0),
    status: String(v2.status ?? '').toLowerCase(),
    // Django lifecycle bucket used by Hub status chips (Active Works /
    // Ongoing / Not Started). Dropping this left every default chip
    // matching zero rows while /api/v2/jobs/ still returned data.
    combined_status: v2.combined_status != null
      ? String(v2.combined_status).toLowerCase()
      : null,
    win_status: nullableString(v2.win_status),
    is_starred: v2.is_starred === true,
    current_stage: nullableString(v2.current_stage),
    stage_sub_status: nullableString(v2.stage_sub_status),
    stage: nullableString(v2.stage ?? v2.current_stage),
    hold_reason: nullableString(v2.hold_reason),
    notes: nullableString(v2.notes),
    year: yearOf(v2.year, v2.start_date),
    parent_job_id: v2.parent_job_id != null ? num(v2.parent_job_id) : null,
    child_count: v2.child_count != null ? num(v2.child_count) : 0,
    hierarchy: hierarchy
      ? { parent_job_id: nullableNumber(hierarchy.parent_job_id), child_count: num(hierarchy.child_count) }
      : (v2.parent_job_id != null || v2.child_count != null
          ? { parent_job_id: nullableNumber(v2.parent_job_id), child_count: num(v2.child_count) }
          : null),
    access: access ? {
      ...(access.level != null ? { level: String(access.level) } : {}),
      ...(access.accessLevel != null ? { accessLevel: String(access.accessLevel) } : {}),
      ...(typeof access.can_edit === 'boolean' ? { can_edit: access.can_edit } : {}),
      ...(typeof access.can_delete === 'boolean' ? { can_delete: access.can_delete } : {}),
      ...(typeof access.canEdit === 'boolean' ? { canEdit: access.canEdit } : {}),
      ...(typeof access.canDelete === 'boolean' ? { canDelete: access.canDelete } : {}),
    } : null,
    created_at: String(v2.created_at ?? ''),
    start_date: nullableString(v2.start_date),
    end_date: nullableString(v2.end_date),
    percent_complete: v2.percent_complete != null ? num(v2.percent_complete) : null,
    project_manager: nullableString(v2.project_manager_name),
    last_activity_date: nullableString(v2.last_activity_date),
    last_timesheet_date: nullableString(v2.last_timesheet_date),
    // W5a served KPI fields ride through untouched — the servedJobMetrics
    // readers prefer them over any client fallback math.
    wip_earned: nullableNumber(v2.wip_earned),
    wip_balance: nullableNumber(v2.wip_balance ?? v2.wip),
    at_risk_overdue: typeof v2.at_risk_overdue === 'boolean' ? v2.at_risk_overdue : null,
    health_score: nullableNumber(v2.health_score),
    health_band: nullableString(v2.health_band),
    // Served as `{}` when the org defines no custom fields, so an absent key
    // and an empty map are the same thing to every reader downstream.
    custom_fields: customFields(v2.custom_fields),
  }
}

/**
 * Full payload reshape. Accepts the DRF paginated envelope ({results: [...]})
 * or a bare array so a pagination-config change never breaks the list —
 * same defensive shape reshapeFilesPayload uses for the files union.
 */
export function reshapeV2JobsPayload(data: unknown): JobListRow[] {
  const rawRows = Array.isArray(data)
    ? data
    : (isRecord(data) && Array.isArray(data.results) ? data.results : [])
  return rawRows.filter(isRecord).map(mapV2JobToRow)
}

/** Preserve DRF pagination metadata while adapting only the result rows. */
export function reshapeV2JobsPage(data: unknown): JobsListPage {
  if (Array.isArray(data)) {
    const results = reshapeV2JobsPayload(data)
    return { count: results.length, next: null, previous: null, results }
  }
  if (!isRecord(data)) {
    return { count: 0, next: null, previous: null, results: [] }
  }
  const results = reshapeV2JobsPayload(data)
  const count = Number(data.count)
  return {
    count: Number.isSafeInteger(count) && count >= results.length ? count : results.length,
    next: typeof data.next === 'string' ? data.next : null,
    previous: typeof data.previous === 'string' ? data.previous : null,
    results,
  }
}
